import React, { useState, useEffect } from "react";
import {
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  TextField,
  ComboBox,
  IComboBoxOption,
  IComboBox,
} from "@fluentui/react";
import { Customer, getAllActiveCustomers } from "@/api/customers";

export interface FileFilterOptions {
  statusFilter: "all" | "active" | "inactive";
  selectedCustomer: Customer | null;
  fileSizeMin: string;
  fileSizeMax: string;
}

export interface FileFilterPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  selectedFilters: FileFilterOptions;
  onFiltersChange: (filters: FileFilterOptions) => void;
  onApplyFilters: (filters: FileFilterOptions) => void;
  onClearFilters: () => void;
}

const FileFilterPanel: React.FC<FileFilterPanelProps> = ({
  isOpen,
  onDismiss,
  selectedFilters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const [localFilters, setLocalFilters] =
    useState<FileFilterOptions>(selectedFilters);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerOptions, setCustomerOptions] = useState<IComboBoxOption[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState<boolean>(false);

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApplyFilters(localFilters);
    onDismiss();
  };

  const handleClear = () => {
    const clearedFilters: FileFilterOptions = {
      statusFilter: "all",
      selectedCustomer: null,
      fileSizeMin: "",
      fileSizeMax: "",
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onClearFilters();
    onApplyFilters(clearedFilters); // Apply the cleared filters to trigger server call
    onDismiss();
  };

  const handleStatusChange = (option: "all" | "active" | "inactive") => {
    setLocalFilters((prev) => ({ ...prev, statusFilter: option }));
  };

  const handleCustomerChange = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption
  ) => {
    if (option) {
      const selectedCustomer =
        customers.find((c) => c.Id === option.key) || null;
      setLocalFilters((prev) => ({ ...prev, selectedCustomer }));
    } else {
      setLocalFilters((prev) => ({ ...prev, selectedCustomer: null }));
    }
  };

  const handleFileSizeMinChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setLocalFilters((prev) => ({ ...prev, fileSizeMin: newValue || "" }));
  };

  const handleFileSizeMaxChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setLocalFilters((prev) => ({ ...prev, fileSizeMax: newValue || "" }));
  };

  const hasActiveFilters = () => {
    return (
      localFilters.statusFilter !== "all" ||
      localFilters.selectedCustomer !== null ||
      localFilters.fileSizeMin.trim() !== "" ||
      localFilters.fileSizeMax.trim() !== ""
    );
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedFilters.statusFilter !== "all") count++;
    if (selectedFilters.selectedCustomer !== null) count++;
    if (
      selectedFilters.fileSizeMin.trim() !== "" ||
      selectedFilters.fileSizeMax.trim() !== ""
    )
      count++;
    return count;
  };

  // Load customers when panel opens
  useEffect(() => {
    if (isOpen && customers.length === 0) {
      setLoadingCustomers(true);
      getAllActiveCustomers()
        .then((result) => {
          if (result && Array.isArray(result)) {
            setCustomers(result);
            // Create options for ComboBox
            const options: IComboBoxOption[] = result.map((customer) => ({
              key: customer.Id,
              text: `${customer.Name} (ID: ${customer.Id})`,
              data: customer,
            }));
            setCustomerOptions(options);
          }
        })
        .catch((error) => {
          console.error("Failed to load customers:", error);
        })
        .finally(() => {
          setLoadingCustomers(false);
        });
    }
  }, [isOpen, customers.length]);

  // Update local filters when selectedFilters prop changes
  React.useEffect(() => {
    setLocalFilters(selectedFilters);
  }, [selectedFilters]);

  return (
    <Panel
      headerText="Filter Files"
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.smallFixedFar}
      closeButtonAriaLabel="Close filter panel"
      isLightDismiss={true}
    >
      <div style={{ padding: "20px 0" }}>
        {/* Status Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>Status</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Checkbox
              label="All"
              checked={localFilters.statusFilter === "all"}
              onChange={() => handleStatusChange("all")}
            />
            <Checkbox
              label="Active"
              checked={localFilters.statusFilter === "active"}
              onChange={() => handleStatusChange("active")}
            />
            <Checkbox
              label="Inactive"
              checked={localFilters.statusFilter === "inactive"}
              onChange={() => handleStatusChange("inactive")}
            />
          </div>
        </div>

        {/* Customer Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>Customer</h4>
          <ComboBox
            placeholder={
              loadingCustomers ? "Loading customers..." : "Select a customer"
            }
            options={customerOptions}
            selectedKey={localFilters.selectedCustomer?.Id || null}
            onChange={handleCustomerChange}
            disabled={loadingCustomers}
            allowFreeform={false}
            autoComplete="on"
            styles={{
              root: { width: "100%" },
            }}
          />
        </div>

        {/* File Size Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>
            File Size (KB)
          </h4>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <TextField
              placeholder="Min"
              value={localFilters.fileSizeMin}
              onChange={handleFileSizeMinChange}
              type="number"
              styles={{
                root: { flex: 1 },
              }}
            />
            <span style={{ color: "#605e5c" }}>to</span>
            <TextField
              placeholder="Max"
              value={localFilters.fileSizeMax}
              onChange={handleFileSizeMaxChange}
              type="number"
              styles={{
                root: { flex: 1 },
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "30px",
            paddingTop: "20px",
            borderTop: "1px solid #edebe9",
          }}
        >
          <PrimaryButton
            text="Apply Filters"
            onClick={handleApply}
          />
          <DefaultButton
            text="Clear All"
            onClick={handleClear}
            disabled={!hasActiveFilters()}
          />
        </div>

        {getActiveFiltersCount() > 0 && (
          <div
            style={{
              marginTop: "15px",
              fontSize: "12px",
              color: "#605e5c",
            }}
          >
            Active filters: {getActiveFiltersCount()} filter(s) applied
          </div>
        )}
      </div>
    </Panel>
  );
};

export default FileFilterPanel;
